import { useEffect, useMemo } from 'react';

import {
	type ConversationAction,
	useConversationActionsStoreActions,
} from '@atlassian/rovo-conversation-actions';

import {
	type BaseAIAction,
	type BaseAISmartButtonContext,
	type BaseAutomationsConfig,
	type BaseNudge,
	useAISmartButtonActions,
} from '../../ai-smart-button-store';

import { mapActionsToConversationActions } from './utils';

/**
 * Hook for managing actions associated with a specific source.
 * Automatically handles cleanup on unmount.
 */
export const useSourceAIActions = (sourceId: string) => {
	const {
		removeContext,
		removeAll,
		removeAutomationsConfig,
		setActions,
		setNudges,
		setNudgesRefreshed,
		setSuggestedActions,
		setContext,
		setAutomationsConfig,
	} = useAISmartButtonActions();

	const { setConversationActions } = useConversationActionsStoreActions();

	useEffect(() => {
		// Cleanup on unmount.
		return () => {
			removeAll(sourceId);
			removeContext(sourceId);
			removeAutomationsConfig(sourceId);
		};
	}, [sourceId, removeAll, removeContext, removeAutomationsConfig]);

	return useMemo(
		() => ({
			/**
			 * Replaces all actions for this source with new ones.
			 */
			setActions: (actions: BaseAIAction[]) => {
				setActions(sourceId, actions);

				// TODO: delete this once we migrate consumers to new way of setting actions
				if (sourceId !== 'view-page' && sourceId !== 'editor') {
					setConversationActions(mapActionsToConversationActions(actions));
				}
			},

			/*
			 * Replaces all nudges for this source with new ones.
			 */
			setNudges: (nudges: BaseNudge[]) => {
				setNudges(sourceId, nudges);

				// TODO: delete this once we migrate consumers to new way of setting nudges
				if (sourceId === 'jira-ai-rovo-button') {
					setNudgesRefreshed(mapActionsToConversationActions(nudges));
				}
			},

			/*
			 * Replaces all nudges for this source with new ones.
			 */
			setNudgesRefreshed: (nudges: ConversationAction[]) => {
				setNudgesRefreshed(nudges);
			},

			/**
			 * Replaces all suggested actions for this source with new ones.
			 */
			setSuggestedActions: (suggestedActions: BaseAIAction[]) => {
				setSuggestedActions(sourceId, suggestedActions);
			},

			/**
			 * Sets context for this source.
			 */
			setContext: (context: BaseAISmartButtonContext) => setContext({ ...context, sourceId }),

			/**
			 * Sets automations config for this source.
			 */
			setAutomationsConfig: (automationsConfig: BaseAutomationsConfig) =>
				setAutomationsConfig({ ...automationsConfig, sourceId }),

			/**
			 * Removes context for this source.
			 */
			removeContext: () => removeContext(sourceId),

			/**
			 * Removes automations config for this source.
			 */
			removeAutomationsConfig: () => removeAutomationsConfig(sourceId),

			/**
			 * Removes all actions for this source.
			 */
			removeAll: () => removeAll(sourceId),
		}),
		[
			sourceId,
			removeAll,
			removeContext,
			removeAutomationsConfig,
			setActions,
			setNudges,
			setNudgesRefreshed,
			setSuggestedActions,
			setContext,
			setAutomationsConfig,
			setConversationActions,
		],
	);
};

export type SourceAIActions = ReturnType<typeof useSourceAIActions>;
